import { useEffect, useMemo, useRef, useState } from 'react';
import CgDialog from 'modules/Common/ui/CgDialog';
import { z } from 'zod';
import Decimal from 'decimal.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { useOrganization } from '@clerk/clerk-react';

import { OrganizationType, CountryDropdown } from 'modules/Common/utils/Utility';
import http from 'helpers/requests';
import { cgToast } from 'modules/Common/ui/toast';
import { issuesOf } from 'helpers/serverIssues';
import useOperatorInstallationsStore from '../stores/operatorInstallationsStore';
import { useCreateOperatorInstallation } from '../hooks/useCreateOperatorInstallation';
import { useUpdateOperatorInstallation } from '../hooks/useUpdateOperatorInstallation';
import { useFreemium } from 'modules/Common/freemium/FreemiumContext';
import { validatePhoneNumber } from 'modules/Common/config/ConsultantOperatorInfoFormConfig';

import {
  Form,
  FormSection,
  FormField,
  FormActions,
  TextField,
  CountrySelectField,
  FilterSelectField,
  PhoneField,
} from 'modules/Common/form/v2';
import { sectionTitleClass } from 'modules/Common/form/v2/primitives/inputClasses';
import { V } from 'helpers/validationMessages';
import { anchorProps } from 'modules/Common/utils/gapAnchor';
import { foldNeeded, recordFieldAnchor } from 'modules/Common/utils/recordLanding';

// ---- helpers ---------------------------------------------------------------

// Backend stores latitude/longitude as numbers; the form keeps them as
// strings so the user can type freely. Decimal.toString avoids weird
// scientific-notation surprises on round-trip.
const toStr = (v) => {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'number') return new Decimal(v).toString();
  return String(v);
};

function defaultsFor(installation, isConsultant) {
  if (!installation) {
    return {
      name: '',
      internalId: '',
      cbamRegistryId: '',
      distributor: '',
      country: '',
      city: '',
      street: '',
      postcode: '',
      POBox: '',
      UN_LOCODE: '',
      latitude: '',
      longitude: '',
      authorizedRepName: '',
      authorizedRepPhone: '',
      authorizedRepEmail: '',
      ...(isConsultant && { operator: '' }),
    };
  }
  const a = installation.address || {};
  const r = installation.authorizedRep || {};
  let operatorId = installation.independentOperatorId;
  if (operatorId && typeof operatorId === 'object' && operatorId._id) {
    operatorId = operatorId._id;
  }
  return {
    name: installation.name || '',
    internalId: installation.internalId || '',
    cbamRegistryId: installation.cbamRegistryId || '',
    distributor: installation.distributor || '',
    country: a.country != null ? String(a.country) : '',
    city: a.city || '',
    street: a.street || '',
    postcode: a.postcode || '',
    POBox: a.POBox || '',
    UN_LOCODE: a.UN_LOCODE || '',
    latitude: toStr(a.latitude),
    longitude: toStr(a.longitude),
    authorizedRepName: r.name || '',
    authorizedRepPhone: r.phone || '',
    authorizedRepEmail: r.email || '',
    ...(isConsultant && { operator: operatorId || '' }),
  };
}

function payloadFromValues(values, isConsultant) {
  const payload = {
    name: values.name,
    internalId: values.internalId || undefined,
    cbamRegistryId: values.cbamRegistryId || undefined,
    distributor: values.distributor || undefined,
    address: {
      country: parseInt(values.country, 10),
      city: values.city,
      street: values.street,
      POBox: values.POBox,
      postcode: values.postcode,
      UN_LOCODE: values.UN_LOCODE || undefined,
      latitude: values.latitude || undefined,
      longitude: values.longitude || undefined,
    },
    authorizedRep: {
      name: values.authorizedRepName,
      phone: values.authorizedRepPhone,
      email: values.authorizedRepEmail,
    },
  };
  if (isConsultant) payload.independentOperatorId = values.operator;
  return payload;
}

const max = (n, label) => z.string().max(n, `${label} cannot exceed ${n} characters`);
const required = (msg = V.required) => z.string().min(1, msg);

function buildSchema({ requireOperator }) {
  return z.object({
    operator: requireOperator ? required(V.required) : z.string().optional(),
    name: required().pipe(max(256, 'This field')),
    internalId: max(256, 'This field').optional().or(z.literal('')),
    cbamRegistryId: max(256, 'This field').optional().or(z.literal('')),
    distributor: max(256, 'This field').optional().or(z.literal('')),
    country: required().or(z.number().transform(String)),
    city: required().pipe(max(35, 'This field')),
    street: required().pipe(max(35, 'This field')),
    postcode: required().pipe(max(17, 'This field')),
    POBox: required().pipe(max(70, 'This field')),
    UN_LOCODE: max(17, 'This field').optional().or(z.literal('')),
    latitude: max(17, 'This field').optional().or(z.literal('')),
    longitude: max(17, 'This field').optional().or(z.literal('')),
    authorizedRepName: z.string().optional().or(z.literal('')),
    authorizedRepPhone: z
      .string()
      .optional()
      .or(z.literal(''))
      .refine((v) => !v || validatePhoneNumber(v), { message: V.invalidPhone }),
    authorizedRepEmail: z
      .string()
      .optional()
      .or(z.literal(''))
      .refine((v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), {
        message: V.invalidEmail,
      }),
  });
}

// One-line collapse toggle modeled on StockSupplierModal's "Optional
// details" affordance — small uppercase label + chevron, full-width
// rounded slate panel that visually sits inside the form.
function CollapseToggle({ open, onToggle, children }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="w-full flex items-center justify-between text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 border border-slate-200 rounded-xl px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors"
    >
      <span>{children}</span>
      <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
    </button>
  );
}

function CollapsePanel({ open, children }) {
  return (
    <div
      className={`overflow-hidden transition-all ease-in-out ${
        open ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
      }`}
      style={{ transitionDuration: '300ms' }}
    >
      <div className="pt-2">{children}</div>
    </div>
  );
}

// Hook: fetch the consultant's bound operators only when the modal opens
// for a consultant org. Cached for the lifetime of the modal.
function useOperatorOptions(enabled) {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await http.get('/consultants/operators');
        if (cancelled) return;
        setOptions((res.data?.operators || []).map((o) => ({ value: o._id, label: o.name })));
      } catch (err) {
        cgToast.apiError(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return options;
}

// ---- component -------------------------------------------------------------

export default function CreateEditOperatorInstallation({
  canCreate,
  canManage,
  focusFields,
  onSaved,
}) {
  const { triggerLimitExceeded } = useFreemium(); // eslint-disable-line no-unused-vars
  const { organization } = useOrganization();
  const { editInstallation, showCreateEditModal, closeCreateEditModal } =
    useOperatorInstallationsStore();

  const isConsultant = organization?.publicMetadata?.type === OrganizationType.Consultant;
  const isEditing = !!editInstallation;

  const { mutateAsync: createInstallation, isPending: isCreating } = useCreateOperatorInstallation({
    silent: true,
  });
  const { mutateAsync: updateInstallation, isPending: isUpdating } = useUpdateOperatorInstallation({
    silent: true,
  });
  const isLoading = isCreating || isUpdating;

  const operatorOptions = useOperatorOptions(showCreateEditModal && isConsultant);
  const schema = useMemo(() => buildSchema({ requireOperator: isConsultant }), [isConsultant]);
  const defaults = useMemo(
    () => defaultsFor(editInstallation, isConsultant),
    [editInstallation, isConsultant],
  );

  const focusFieldsRef = useRef(focusFields);
  focusFieldsRef.current = focusFields;
  const formRef = useRef(null);

  // Optional sections start collapsed. On edit, auto-open if the loaded
  // installation already has data in those fields so the user
  // immediately sees what they previously filled in. Reset on each open.
  const [showAddressDetails, setShowAddressDetails] = useState(false);
  const [showAuthorizedRep, setShowAuthorizedRep] = useState(false);

  useEffect(() => {
    if (!showCreateEditModal) return;
    const a = editInstallation?.address || {};
    const r = editInstallation?.authorizedRep || {};
    setShowAddressDetails(
      Boolean(a.UN_LOCODE || a.latitude || a.longitude || editInstallation?.distributor) ||
        foldNeeded(focusFieldsRef.current),
    );
    setShowAuthorizedRep(Boolean(r.name || r.phone || r.email));
  }, [showCreateEditModal, editInstallation]);

  if (isEditing && !canManage) return null;
  if (!isEditing && !canCreate) return null;

  async function handleSubmit(values) {
    const payload = payloadFromValues(values, isConsultant);
    try {
      if (isEditing) {
        await updateInstallation({
          installationId: editInstallation._id,
          installationData: payload,
        });
      } else {
        await createInstallation(payload);
      }
    } catch (err) {
      // Server zod issues render inline under their fields (phase 4 seam);
      // unmatched issues and non-validation failures keep the toast (D17f).
      const unmatched = formRef.current?.setServerIssues?.(err);
      if (!unmatched || unmatched.length > 0 || issuesOf(err).length === 0) {
        cgToast.apiError(err);
      }
      return;
    }
    onSaved?.();
    closeCreateEditModal();
  }

  const handleClose = () => {
    if (!isLoading) closeCreateEditModal();
  };

  return (
    <CgDialog
      open={showCreateEditModal}
      onOpenChange={(open) => {
        if (!open && !isLoading) closeCreateEditModal();
      }}
      title={`${isEditing ? 'Edit' : 'New'} Installation`}
      width={720}
      closeOnOverlayClick={false}
    >
      <p
        className={`${sectionTitleClass} mb-1`}
        {...anchorProps('recordModal', 'ownInstallation')}
      >
        {isEditing ? 'Update Installation' : 'Add a new installation'}
      </p>
      <p className="text-sm text-slate-500 mb-5">
        Fill in the basic information below. Address details and the authorized representative are
        optional and live behind the toggles.
      </p>

      <Form
        ref={formRef}
        // Re-key on installation id (or 'new' for create) so RHF picks
        // up freshly-resolved defaults when the modal switches between
        // installations or between create/edit modes.
        key={editInstallation?._id || 'new'}
        schema={schema}
        defaultValues={defaults}
        onSubmit={handleSubmit}
      >
        <FormSection title="Basic Information">
          {isConsultant ? (
            <FormField
              name="operator"
              label="Operator"
              required
              colSpan={6}
            >
              {/* FilterSelectField (Radix popover), not SearchSelectField:
                  react-select's MenuPortal computes viewport coordinates, and
                  CgDialog's content node is fixed + translated, so the menu
                  landed offset by the dialog's own position. Same swap as
                  CountrySelectField below. */}
              <FilterSelectField
                options={operatorOptions}
                placeholder="Select your Operator"
              />
            </FormField>
          ) : null}

          <FormField
            name="name"
            anchor={recordFieldAnchor('name')}
            label="Name"
            required
            colSpan={6}
          >
            <TextField placeholder="Installation name" />
          </FormField>
          <FormField
            name="internalId"
            label="Internal ID"
            colSpan={6}
          >
            <TextField placeholder="e.g. INST-001 (optional)" />
          </FormField>
          <FormField
            name="cbamRegistryId"
            anchor={recordFieldAnchor('cbamRegistryId')}
            label="CBAM Registry ID"
            colSpan={6}
          >
            <TextField placeholder="Unique installation identifier in the CBAM Registry" />
          </FormField>

          <FormField
            name="country"
            anchor={recordFieldAnchor('address.country')}
            label="Country"
            required
            colSpan={6}
          >
            <CountrySelectField
              scope={CountryDropdown.CBAM}
              valueAs="id"
            />
          </FormField>
          <FormField
            name="city"
            anchor={recordFieldAnchor('address.city')}
            label="City"
            required
            colSpan={6}
          >
            <TextField />
          </FormField>

          <FormField
            name="street"
            label="Street Number"
            required
            colSpan={6}
          >
            <TextField />
          </FormField>
          <FormField
            name="postcode"
            anchor={recordFieldAnchor('address.postcode')}
            label="Post Code"
            required
            colSpan={6}
          >
            <TextField />
          </FormField>

          <FormField
            name="POBox"
            label="P.O. Box"
            required
            colSpan={12}
          >
            <TextField />
          </FormField>
        </FormSection>

        <div className="space-y-3 mb-5">
          <CollapseToggle
            open={showAddressDetails}
            onToggle={() => setShowAddressDetails((v) => !v)}
          >
            More address details
          </CollapseToggle>
          <CollapsePanel open={showAddressDetails}>
            <FormSection compact>
              <FormField
                name="UN_LOCODE"
                anchor={recordFieldAnchor('address.UN_LOCODE')}
                label="UNLOCODE"
                colSpan={6}
              >
                <TextField />
              </FormField>
              <FormField
                name="distributor"
                label="Name of Producer"
                colSpan={6}
                popoverContent="This field is for distributors to prevent sharing information about their suppliers. It does not appear in the downloaded CBAM report."
              >
                <TextField />
              </FormField>
              <FormField
                name="latitude"
                anchor={recordFieldAnchor('address.latitude')}
                label="Latitude of the main emissions source"
                colSpan={6}
              >
                <TextField inputMode="decimal" />
              </FormField>
              <FormField
                name="longitude"
                anchor={recordFieldAnchor('address.longitude')}
                label="Longitude of the main emissions source"
                colSpan={6}
              >
                <TextField inputMode="decimal" />
              </FormField>
            </FormSection>
          </CollapsePanel>

          <CollapseToggle
            open={showAuthorizedRep}
            onToggle={() => setShowAuthorizedRep((v) => !v)}
          >
            Authorized representative
          </CollapseToggle>
          <CollapsePanel open={showAuthorizedRep}>
            <FormSection compact>
              <FormField
                name="authorizedRepName"
                label="Name"
                colSpan={12}
              >
                <TextField />
              </FormField>
              <FormField
                name="authorizedRepPhone"
                label="Phone Number"
                colSpan={6}
              >
                <PhoneField />
              </FormField>
              <FormField
                name="authorizedRepEmail"
                label="Email"
                colSpan={6}
              >
                <TextField type="email" />
              </FormField>
            </FormSection>
          </CollapsePanel>
        </div>

        <FormActions
          submitLabel={isEditing ? 'Save Changes' : 'Create'}
          cancelLabel="Cancel"
          onCancel={handleClose}
          loading={isLoading}
        />
      </Form>
    </CgDialog>
  );
}
